import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { GripHorizontal, X } from 'lucide-react';
import CircuitList from '../CircuitList';
import { OVERLAY_ATTRIBUTE } from '../board-grid/useZoomPan';
import type { CircuitHold, RoleOf } from '../circuit';
import type { BoardHand } from '../../../types/device';

interface CircuitOverlayPanelProps<T extends CircuitHold> {
  holds: T[];
  onChange: (holds: T[]) => void;
  /** How this wall reads a hold's role back — see CircuitList. */
  roleOf: RoleOf<T>;
  /** And where the hold is, where the wall can name it (a board names its cell). */
  nameOf?: (hold: T) => string;
  /** Which circles are in hand on the photo, so panel and picture agree. */
  selected: number[];
  onSelect: (index: number) => void;
  forcedHands: boolean;
  onForcedHandsChange: (on: boolean) => void;
  onStartingHandChange: (hand: BoardHand) => void;
  onClose: () => void;
  /**
   * In the column beside the picture (`ClimbEditorSplit`) rather than over it:
   * nothing of the wall is under the list there, so there is nothing to drag it
   * off, and it takes the column's width.
   */
  docked?: boolean;
}

/**
 * How much of the panel has to stay in the window, whichever way it is dragged —
 * enough of the handle to take hold of it again.
 */
const KEEP_IN_VIEW_PX = 64;

/** Where the panel has been dragged to, in pixels off where it opens. */
interface PanelOffset {
  x: number;
  y: number;
}

/** What a drag is measured from: where the pointer went down, and the offset then. */
interface PanelDrag extends PanelOffset {
  fromX: number;
  fromY: number;
}

/**
 * The circuit read back, over the wall it is painted on: whether the hands are
 * forced, and the holds in the order they are climbed.
 *
 * Half the width of the picture and nearly its height, **opening in the middle of
 * it and draggable from there, off the picture included** — with nothing behind it
 * but the wall. The setter is checking a sequence *against* the holds it runs
 * through, so it has to be possible to put the list beside whichever part of the
 * wall the run is on, or beside the wall altogether: the middle is where the eye
 * already is, and the drag is for whatever it happens to be covering. The list
 * itself is on an opaque card, because a number has to be legible over a
 * photograph; the space around it is not.
 *
 * It opens from the button beside the circuit switch rather than standing open:
 * painting the circuit happens on the picture, and this is for looking at what
 * was painted.
 */
export function CircuitOverlayPanel<T extends CircuitHold>({
  holds,
  onChange,
  roleOf,
  nameOf,
  selected,
  onSelect,
  forcedHands,
  onForcedHandsChange,
  onStartingHandChange,
  onClose,
  docked = false,
}: CircuitOverlayPanelProps<T>) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [offset, setOffset] = useState<PanelOffset>({ x: 0, y: 0 });
  // Where the drag began, read as the pointer moves rather than as it was set up.
  const dragRef = useRef<PanelDrag | null>(null);

  /**
   * How far the panel may travel from where the pointer picked it up.
   *
   * **Off the picture is allowed** — the wall is what the list is being read
   * against, so the setter has to be able to put it beside the photo rather than
   * on top of it. What is not allowed is off the *window*: a panel dragged out of
   * the page is a panel nobody can bring back, so a strip of it stays in view on
   * every side.
   *
   * Measured at the moment of the press. Where nothing can be measured — a
   * browserless render — it is left free rather than pinned to nothing.
   */
  const travel = () => {
    const panel = panelRef.current?.getBoundingClientRect();
    if (!panel || panel.width === 0) return null;

    return {
      left: KEEP_IN_VIEW_PX - panel.right,
      right: window.innerWidth - KEEP_IN_VIEW_PX - panel.left,
      top: -panel.top,
      bottom: window.innerHeight - KEEP_IN_VIEW_PX - panel.top,
    };
  };

  const startDrag = (event: ReactPointerEvent) => {
    event.stopPropagation();
    dragRef.current = { fromX: event.clientX, fromY: event.clientY, ...offset };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const drag = (event: ReactPointerEvent) => {
    const from = dragRef.current;
    if (!from) return;

    const moved = { x: event.clientX - from.fromX, y: event.clientY - from.fromY };
    const room = travel();

    setOffset({
      x: from.x + (room ? clamp(moved.x, room.left, room.right) : moved.x),
      y: from.y + (room ? clamp(moved.y, room.top, room.bottom) : moved.y),
    });
  };

  const endDrag = (event: ReactPointerEvent) => {
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  return (
    <div
      ref={panelRef}
      // In the middle of the picture, clear of the rows of overlay buttons along
      // the top of it, and moved from there by whatever the handle was dragged.
      // A column, so the card inside can keep its handle still and scroll the run
      // under it rather than the two going together. Docked, the same column at
      // the top of the fields, short enough that a long run scrolls inside it
      // rather than pushing the fields off the screen.
      className={
        docked
          ? 'flex max-h-[60dvh] w-full flex-col'
          : 'absolute bottom-2 left-1/2 top-14 z-20 flex w-1/2 flex-col'
      }
      style={
        docked ? undefined : { transform: `translate(calc(-50% + ${offset.x}px), ${offset.y}px)` }
      }
      // On the picture rather than in it: a press in here is reading the circuit,
      // not drawing on the wall or moving the photo under it.
      {...{ [OVERLAY_ATTRIBUTE]: '' }}
      onPointerDown={(event) => event.stopPropagation()}
      data-testid="circuit-overlay-panel"
    >
      {/* `min-h-0` so the card may be shorter than its contents: without it the
          run would push the handle off the top of the picture instead of scrolling
          under it. A short circuit leaves the card the height of what is in it. */}
      <div className="flex min-h-0 flex-col rounded-md bg-background/95 shadow-lg backdrop-blur">
        {/* Where the panel is taken hold of, and the way out — the top edge of the
            card itself, immediately over the first thing in it, and **still there
            when the run has been scrolled**: it is what the panel is moved and
            closed by, so it cannot be something to scroll back up for. Everything
            else the circuit answers for — whether the hands are forced, which hand
            it starts on, the order itself — belongs to the list under it. */}
        <div className="flex shrink-0 items-center gap-2 border-b px-2 py-1">
          {/* Docked there is nowhere to move it to: the way out alone. */}
          {docked ? (
            <span className="flex-1" />
          ) : (
            <div
              role="button"
              tabIndex={0}
              aria-label={t('climbEditor.circuit.move')}
              title={t('climbEditor.circuit.move')}
              // `touch-none` because the browser settles what a touch may do when
              // the finger lands: without it the first drag downwards is given to
              // the page as a scroll and the panel is left behind.
              className="flex flex-1 cursor-grab touch-none items-center justify-center text-muted-foreground active:cursor-grabbing"
              onPointerDown={startDrag}
              onPointerMove={drag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              data-testid="circuit-panel-handle"
            >
              <GripHorizontal className="size-4" aria-hidden />
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            aria-label={t('climbEditor.actions.close')}
            className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-2" data-testid="circuit-panel-scroll">
          <CircuitList
            holds={holds}
            onChange={onChange}
            roleOf={roleOf}
            nameOf={nameOf}
            selected={selected}
            onSelect={onSelect}
            forcedHands={forcedHands}
            onForcedHandsChange={onForcedHandsChange}
            onStartingHandChange={onStartingHandChange}
          />
        </div>
      </div>
    </div>
  );
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}
